import { createContext, useContext, useCallback, useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";

const ThemeContext = createContext({ theme: "dark", toggleTheme: () => {}, setTheme: () => {} });

const STORAGE_KEY = "talmia_theme";

// Universal light mode: attributes live on <html> so every page, portal and pop-up shares one palette.
function applyTheme(t) {
  const el = document.documentElement;
  if (t === "light") {
    el.setAttribute("data-talmia-theme", "light");
    el.setAttribute("data-quote-builder-theme", "light");
    el.style.colorScheme = "light";
  } else {
    el.removeAttribute("data-talmia-theme");
    el.removeAttribute("data-quote-builder-theme");
    el.style.colorScheme = "dark";
  }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    let t = "dark";
    try { t = localStorage.getItem(STORAGE_KEY) || "dark"; } catch { /* ignore */ }
    applyTheme(t);
    return t;
  });

  // Universal light mode: one switch for every screen — attributes live on <html>
  // so pages, portals, pop-ups and the shell all pick up the same palette.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
    applyTheme(theme);
  }, [theme]);

  // Attribute is written BEFORE the state change so JS palettes read the new theme during that render.
  const setTheme = useCallback((t) => { const n = t === "light" ? "light" : "dark"; applyTheme(n); setThemeState(n); }, []);
  const toggleTheme = useCallback(() => {
    const n = document.documentElement.getAttribute("data-talmia-theme") === "light" ? "dark" : "light";
    applyTheme(n); setThemeState(n);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

// Sun/Moon toggle for the app header.
export function ThemeToggle({ className = "" }) {
  const { theme, toggleTheme } = useTheme();
  const isLight = theme === "light";
  return (
    <button
      type="button"
      onClick={toggleTheme}
      data-testid="theme-toggle"
      aria-label={isLight ? "Switch to dark mode" : "Switch to light mode"}
      title={isLight ? "Switch to dark mode" : "Switch to light mode"}
      className={`w-9 h-9 grid place-items-center rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors ${className}`}
    >
      {isLight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
    </button>
  );
}
